<script setup>
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Inplace from 'primevue/inplace';
import InputText from 'primevue/inputtext';
import Card from '@/components/Card.vue';
import NadaEncontrado from '@/components/NadaEncontrado.vue';
import { ordenarArrayDeObjetos } from '@/utils/ordenarArrayDeObjetos';
import { useRecursosDidaticosStore } from "@/store/recursosDidaticosStore";
import { carregarQuestoesDaAvalicao, excluirQuestaoDaLista, atualizarTituloAvalicao } from '@/api/fetchAvaliacoes';
import { onMounted } from 'vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ExportarListaQuestoes from './ExportarListaQuestoes.vue';
import { replaceEspacosPorHTML } from '@/utils/replaceEspacosPorHTML';

const store = useRecursosDidaticosStore();
const { avaliacao, gerenciarListaQuestoesVisible, idUsuario, carregandoQuestoes } = storeToRefs(store);


const questoes = ref([]);

const excluindoQuestao = ref(null)

onMounted(async () => {
    carregandoQuestoes.value = true;
    let ids = avaliacao.value.questoes.map(item => item.questao)
    let response = await carregarQuestoesDaAvalicao(ids);
    if (response) {
        response.map(questao => {
            let ordem = avaliacao.value.questoes.find(item => item.questao === questao.id).ordem;
            questao.ordem = ordem
        })

        response = ordenarArrayDeObjetos(response, 'ordem')
        questoes.value = response;
    }
    carregandoQuestoes.value = false

})

async function excluirQuestao(id) {
    excluindoQuestao.value = id
    let data = await excluirQuestaoDaLista(avaliacao.value.id, id);

    if (data) {
        avaliacao.value.questoes = avaliacao.value.questoes.filter(item => item.questao !== id)
        questoes.value = questoes.value.filter(item => item.id !== id)
    }
    excluindoQuestao.value = null;
    
}

async function salvarTitulo(){
  await atualizarTituloAvalicao(avaliacao.value.id, avaliacao.value.titulo);
}
</script>

<template>
    <Dialog v-model:visible="gerenciarListaQuestoesVisible" modal header="Header" class="h-screen w-screen">
        <template #header class="border-bottom-1 surface-border">
            <div class="flex align-items-center gap-2 ">

                <Inplace v-if="avaliacao.proprietario === idUsuario" :closable="true" class="hover:g-white">
                    <template #closeicon>
                        <i class="pi pi-check" @click="salvarTitulo"></i>
                    </template>
                    <template #display>
                        <span class="font-bold">{{ avaliacao.titulo }}</span>
                    </template>
                    <template #content>
                        <InputText v-model="avaliacao.titulo" autofocus />
                    </template>
                </Inplace>
                <div v-else>
                    <span class="font-bold">{{ avaliacao.titulo }}</span>
                </div>

            </div>
        </template>

        <div v-if="!carregandoQuestoes && questoes.length > 0">
            <div class="mx-auto mx-auto max-w-800">
                <div class="flex justify-content-end mb-4 mt-2 ">
                    <ExportarListaQuestoes :questoes="questoes" :titulo="avaliacao.titulo" />
                </div>
                <div class="flex flex-column gap-5 ">
                    <Card v-for="questao in questoes" :key="questao.id">
                        <template #conteudo>
                            <div class="flex flex-column gap-4 ">
                                <img v-if="questao.imagem" :src="questao.imagem" class="w-full" />
                                <div v-if="questao.texto" class="text-base text-color" v-html="replaceEspacosPorHTML(questao.texto)"></div>
                                <div v-if="questao.pergunta" class="font-semibold text-color text-lg">{{
                                    questao.pergunta }}</div>
                                <div v-if="questao.a" class="flex flex-column gap-4">
                                    <div v-if="questao.a" class="flex flex-row gap-3 align-items-center">
                                        <div class="font-semibold border-round-md p-2 surface-200 text-center w-3rem"
                                            :class="{ 'bg-primary-500 text-white': questao.gabarito === 'a' }">
                                            A
                                        </div>
                                        <div class="w-full text-base text-color">
                                            {{ questao.a }}
                                        </div>
                                    </div>
                                    <div v-if="questao.b" class="flex flex-row gap-3 align-items-center">
                                        <div class="font-semibold border-round-md p-2 surface-200 text-center w-3rem"
                                            :class="{ 'bg-primary-500 text-white': questao.gabarito === 'b' }">
                                            B
                                        </div>
                                        <div class="w-full text-base text-color">
                                            {{ questao.b }}
                                        </div>
                                    </div>
                                    <div v-if="questao.c" class="flex flex-row gap-3 align-items-center">
                                        <div class="font-semibold border-round-md p-2 surface-200 text-center w-3rem"
                                            :class="{ 'bg-primary-500 text-white': questao.gabarito === 'c' }">
                                            C
                                        </div>
                                        <div class="w-full text-base text-color">
                                            {{ questao.c }}
                                        </div>
                                    </div>
                                    <div v-if="questao.d" class="flex flex-row gap-3 align-items-center">
                                        <div class="font-semibold border-round-md p-2 surface-200 text-center w-3rem"
                                            :class="{ 'bg-primary-500 text-white': questao.gabarito === 'd' }">
                                            D
                                        </div>
                                        <div class="w-full text-base text-color">
                                            {{ questao.d }}
                                        </div>
                                    </div>
                                    <div v-if="questao.e" class="flex flex-row gap-3 align-items-center">
                                        <div class="font-semibold border-round-md p-2 surface-200 text-center w-3rem"
                                            :class="{ 'bg-primary-500 text-white': questao.gabarito === 'e' }">
                                            E
                                        </div>
                                        <div class="w-full text-base text-color">
                                            {{ questao.e }}
                                        </div>
                                    </div>
                                </div>
                                <div class="flex justify-content-end">
                                    <Button :loading="excluindoQuestao === questao.id"
                                        @click="excluirQuestao(questao.id)" icon="pi pi-trash" label="Excluir" text
                                        severity="secondary" v-if="avaliacao.proprietario === idUsuario" />
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </div>
        </div>
        <NadaEncontrado v-if="!carregandoQuestoes && questoes.length === 0"
            mensagem="👋 Você ainda não adicionou nenhuma questão nessa avaliação" />
        <LoadingSpinner v-if="carregandoQuestoes" />
    </Dialog>
</template>